import type {
  ChecklistItemResponse,
  ChecklistItemStatus,
  ChecklistPriority,
  ChecklistRequestKind,
  ChecklistSectionResponse,
} from '../api/types'
import { CHECKLIST_STATUSES, checklistStatusLabel } from '../lib/checklist'
import {
  AWAITING_MY_REVIEW,
  MY_OPEN_ITEMS,
  NO_FILTERS,
  hasFilters,
  type ChecklistFilters,
} from '../lib/checklistView'

/**
 * Narrows the request list. The filters live in the URL (the caller owns them through
 * useSearchParams), so a narrowed view can be pasted into an email and nothing is stored in the
 * browser. "My open items" and "Awaiting my review" are two filters with a button each, not
 * saved state.
 */
export function ChecklistFilterBar({
  filters,
  onChange,
  sections,
  items,
  shown,
  canManage,
}: {
  filters: ChecklistFilters
  onChange: (next: ChecklistFilters) => void
  sections: ChecklistSectionResponse[]
  items: ChecklistItemResponse[]
  shown: number
  canManage: boolean
}) {
  const set = (patch: Partial<ChecklistFilters>) => onChange({ ...filters, ...patch })
  const categories = [
    ...new Set(items.map((i) => i.category).filter((c): c is string => !!c)),
  ].sort()
  const hasLoose = items.some((i) => i.sectionId === null)

  return (
    <section className="panel checklist-filters" aria-label="Filter the request list">
      <div className="row-actions">
        <button type="button" className="btn btn-sm" onClick={() => onChange(MY_OPEN_ITEMS)}>
          My open items
        </button>
        {canManage && (
          <button type="button" className="btn btn-sm" onClick={() => onChange(AWAITING_MY_REVIEW)}>
            Awaiting my review
          </button>
        )}
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => onChange({ ...NO_FILTERS, overdue: true })}
        >
          Overdue
        </button>
        {hasFilters(filters) && (
          <button type="button" className="btn btn-sm" onClick={() => onChange(NO_FILTERS)}>
            Clear filters
          </button>
        )}
        <span className="small muted" role="status">
          {hasFilters(filters)
            ? `${shown} of ${items.length} requests`
            : `${items.length} requests`}
        </span>
      </div>
      <div className="filter-grid">
        <label className="field">
          <span>Find</span>
          <input
            type="search"
            value={filters.text}
            onChange={(e) => set({ text: e.target.value })}
            placeholder="Label, words or category"
          />
        </label>
        <label className="field">
          <span>Workstream</span>
          <select
            value={filters.section ?? ''}
            onChange={(e) => set({ section: e.target.value || null })}
          >
            <option value="">All</option>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} {s.name}
              </option>
            ))}
            {hasLoose && <option value="none">Uncategorized</option>}
          </select>
        </label>
        <label className="field">
          <span>Category</span>
          <select
            value={filters.category ?? ''}
            onChange={(e) => set({ category: e.target.value || null })}
          >
            <option value="">All</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Status</span>
          <select
            value={filters.status ?? ''}
            onChange={(e) =>
              set({ status: (e.target.value || null) as ChecklistItemStatus | null })
            }
          >
            <option value="">All</option>
            {CHECKLIST_STATUSES.map((s) => (
              <option key={s} value={s}>
                {checklistStatusLabel(s)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Priority</span>
          <select
            value={filters.priority ?? ''}
            onChange={(e) =>
              set({ priority: (e.target.value || null) as ChecklistPriority | null })
            }
          >
            <option value="">All</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </label>
        <label className="field">
          <span>Kind</span>
          <select
            value={filters.kind ?? ''}
            onChange={(e) => set({ kind: (e.target.value || null) as ChecklistRequestKind | null })}
          >
            <option value="">All</option>
            <option value="Static">Static</option>
            <option value="Dynamic">Dynamic</option>
          </select>
        </label>
        <label className="field">
          <span>Documents</span>
          <select
            value={filters.documents === null ? '' : filters.documents ? '1' : '0'}
            onChange={(e) =>
              set({ documents: e.target.value === '' ? null : e.target.value === '1' })
            }
          >
            <option value="">Either</option>
            <option value="1">Has documents</option>
            <option value="0">Has none</option>
          </select>
        </label>
      </div>
      <div className="row-actions">
        <label className="check small">
          <input
            type="checkbox"
            checked={filters.mine}
            onChange={(e) => set({ mine: e.target.checked })}
          />
          <span>Assigned to me</span>
        </label>
        <label className="check small">
          <input
            type="checkbox"
            checked={filters.open}
            onChange={(e) => set({ open: e.target.checked })}
          />
          <span>Still to be answered</span>
        </label>
        <label className="check small">
          <input
            type="checkbox"
            checked={filters.overdue}
            onChange={(e) => set({ overdue: e.target.checked })}
          />
          <span>Overdue</span>
        </label>
      </div>
    </section>
  )
}
